import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/button.component';

import { ExtendedReportList } from '@futrli/views/processors/reports/reportsList';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { LISTING_PAGE_TABS_HEIGHT } from '@futrli/ui-components/components/listing-page/listing-page-tabs/listing-page-tabs';
import { ListingPageHeader } from '@futrli/ui-components/components/listing-page/listing-page-header/listing-page-header';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';

import { DeleteReportDialogContainer } from '../../delete-report-dialog/delete-report-dialog-container';
import { LockReportDialogContainer } from '../../lock-report-dialog/lock-report-dialog-container';
import { PublishReportDialogContainer } from '../../publish-report-dialog/publish-report-dialog-container';
import { useNavHeight } from '../../../../hooks/useNavHeight';
import { VirtualizedCardList } from '../../../../ui/virtualized-card-list/virtualized-card-list';
import { VirtualizedCardListEmptyState } from '../../../../ui/virtualized-card-list/virtualized-card-list-empty-state';
import { TabErrorMessage } from '../error-message/tab-error-message';
import { MY_REPORTS_STORAGE_KEY } from '../shared/sort-config';
import { useLiveReports } from './hooks/use-live-reports';
import { MyReportRow, ROW_HEIGHT_DEFAULT, ROW_HEIGHT_WITH_LOCKED_PILL } from './my-report-row';
import { MyReportsFilters } from './my-reports-filters';
import { useStatusOrientationFilter } from '../shared/use-status-orientation-filter';

export type ReportDialogType = 'lock' | 'publish' | 'delete' | null;

export interface ReportDialogState {
  type: ReportDialogType;
  report: ExtendedReportList | null;
}

const statusAccessor = (item: ExtendedReportList) => item.status;
const nameAccessor = (item: ExtendedReportList) => item.name;
const orientationAccessor = (item: ExtendedReportList) => item.orientation;

export const MyReportsTab = () => {
  const intl = useIntl();
  const { organisationId = '' } = useParams();
  const { navHeight } = useNavHeight();
  const { liveReports, isLoading, errorLoading, isResourceSharingEnabled } = useLiveReports();
  const [activeDialog, setActiveDialog] = useState<ReportDialogState>({ type: null, report: null });

  const { report } = useSidebarContext();

  const openDialog = useCallback((type: ReportDialogType, report: ExtendedReportList) => {
    setActiveDialog({ type, report });
  }, []);

  const closeDialog = useCallback(() => {
    setActiveDialog({ type: null, report: null });
  }, []);

  const {
    filteredData,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    sortValue,
    sortOptions,
    handleSortChange
  } = useStatusOrientationFilter<ExtendedReportList>({
    data: liveReports,
    statusAccessor,
    publishedValue: 'live',
    nameAccessor,
    orientationAccessor,
    storageKey: MY_REPORTS_STORAGE_KEY,
    organisationId,
    isResourceSharingEnabled,
    sharingStatusAccessor: item => item.permission_info?.sharing_status
  });

  const renderRow = useCallback(
    (item: ExtendedReportList) => (
      <MyReportRow
        report={item}
        onDialogAction={openDialog}
        isResourceSharingEnabled={isResourceSharingEnabled}
      />
    ),
    [openDialog, isResourceSharingEnabled]
  );

  const rowHeight = useCallback(
    ({ index }: { index: number }) =>
      !isResourceSharingEnabled && filteredData[index]?.is_locked ? ROW_HEIGHT_WITH_LOCKED_PILL : ROW_HEIGHT_DEFAULT,
    [filteredData, isResourceSharingEnabled]
  );

  return (
    <Box>
      <ListingPageHeader
        variant="tab-header"
        title={<FormattedMessage id="predict.reports_list" defaultMessage="Reports list" />}
        helpUrl="https://support.futrli.com/en/articles/14301535-my-reports"
      />
      {errorLoading ? (
        <TabErrorMessage />
      ) : (
        <>
          <MyReportsFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            typeFilter={typeFilter}
            onTypeChange={setTypeFilter}
            sortValue={sortValue}
            onSortChange={handleSortChange}
            sortOptions={sortOptions}
            stickyTop={navHeight + LISTING_PAGE_TABS_HEIGHT}
            isResourceSharingEnabled={isResourceSharingEnabled}
          />
          <Box mt="24px">
            <VirtualizedCardList<ExtendedReportList>
              data={filteredData}
              renderRow={renderRow}
              rowHeight={rowHeight}
              isLoading={isLoading}
              emptyState={
                <VirtualizedCardListEmptyState
                  title={intl.formatMessage({
                    id: 'reporting.no_reports_to_show',
                    defaultMessage: 'No reports to show'
                  })}
                  description={intl.formatMessage({
                    id: 'reporting.first_report_get_started',
                    defaultMessage: 'Get started with your first report'
                  })}
                  action={
                    <Button type="secondary" onClick={() => report.open()}>
                      <FormattedMessage id="reporting.create_report" defaultMessage="Create Report" />
                    </Button>
                  }
                />
              }
            />
          </Box>
        </>
      )}
      {activeDialog.type === 'lock' && activeDialog.report && (
        <LockReportDialogContainer isOpen onClose={closeDialog} report={activeDialog.report} />
      )}
      {activeDialog.type === 'publish' && activeDialog.report && (
        <PublishReportDialogContainer isOpen onClose={closeDialog} report={activeDialog.report} />
      )}
      {activeDialog.type === 'delete' && activeDialog.report && (
        <DeleteReportDialogContainer isOpen onClose={closeDialog} report={activeDialog.report} />
      )}
    </Box>
  );
};
